import { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { baseUrl } from "../../Utility/constants";
import Footer from "../common/Footer";
import TeacherHeader from "../common/TeacherHeader";
import { Table, Button, Spinner } from "react-bootstrap";
import useTeacherStudents from "../../hooks/useTeacherStudents";
import useCurrentSession from "../../hooks/useCurrentSession";


const StudentLCDetailMappings = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [conductList, setConductList] = useState([]);
  const [progressList, setProgressList] = useState([]);
  const [remarkList, setRemarkList] = useState([]);
  const [reasonList, setReasonList] = useState([]);

  const { students, loading: studentsLoading } = useTeacherStudents();
  const { currentSession, loading: sessionLoading } = useCurrentSession();
  const sessionId = currentSession?.sessionId;

  useEffect(() => {
    if (students.length > 0 && sessionId) {
      initializeData();
    }
  }, [students.length, sessionId]);

  const initializeData = async () => {
    setLoading(true);

    try {
      const [
        conductRes,
        progressRes,
        remarkRes,
        reasonRes,
        mappingRes
      ] = await Promise.all([
        axios.get(`${baseUrl}/api/LCConductMaster`),
        axios.get(`${baseUrl}/api/LCProgressMaster`),
        axios.get(`${baseUrl}/api/LCRemarkMaster`),
        axios.get(`${baseUrl}/api/LCReasonMaster`),
        axios.get(`${baseUrl}/api/StudentsLCMappings/GetBySession/${sessionId}`)
      ]);

      setConductList(conductRes.data);
      setProgressList(progressRes.data);
      setRemarkList(remarkRes.data);
      setReasonList(reasonRes.data);

      const getDefaultId = (list) =>
        list.find((x) => x.isDefault)?.id || null;

      const defaultConduct = getDefaultId(conductRes.data);
      const defaultProgress = getDefaultId(progressRes.data);
      const defaultReason = getDefaultId(reasonRes.data);
      const defaultRemark = getDefaultId(remarkRes.data);

      const existingMappings = mappingRes.data;

      const mappingMap = new Map(
        existingMappings.map(x => [x.studentId, x])
      );

      const mappedStudents = students.map((s) => {
        const existing = mappingMap.get(s.studentId);

        return {
          ...s,
          lcConductMasterId: existing?.lcConductMasterId ?? defaultConduct,
          lcProgressMasterId: existing?.lcProgressMasterId ?? defaultProgress,
          lcReasonMasterId: existing?.lcReasonMasterId ?? defaultReason,
          lcRemarkMasterId: existing?.lcRemarkMasterId ?? defaultRemark
        };
      });

      setData(mappedStudents);

    } catch (error) {
      toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };


  // 🔥 Handle dropdown change
  const handleLCChange = (index, field, value) => {
    const updated = [...data];
    updated[index][field] = Number(value);
    setData(updated);
  };

  // 🔥 Save LC Mapping
  const handleSave = async () => {

    if (!sessionId) {
      toast.error("Session not found");
      return;
    }
    setSaving(true);
    try {
      const createdBy = Number(localStorage.getItem("userID"));

      const payload = data.map((item) => ({
        studentId: item.studentId,
        standardId: item.standardId,
        sectionId: item.sectionId,
        sessionId: sessionId,
        lcConductMasterId: item.lcConductMasterId,
        lcProgressMasterId: item.lcProgressMasterId,
        lcReasonMasterId: item.lcReasonMasterId,
        lcRemarkMasterId: item.lcRemarkMasterId,
        createdBy: createdBy
      }));

      await axios.post(
        `${baseUrl}/api/StudentsLCMappings/SaveBulk`,
        payload
      );

      toast.success("LC Details saved successfully!");
      await axios.get(
        `${baseUrl}/api/StudentsLCMappings/GetBySession/${sessionId}`
      );
    } catch (error) {
      toast.error("Failed to save LC details.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <TeacherHeader />
      <div className="container mt-5 mb-5">
        <div className="d-flex justify-content-between mb-3">
          <h4>Student LC Detail Mapping</h4>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" animation="border" /> : "Save Details"}
          </Button>
        </div>

        {studentsLoading || sessionLoading || loading ? (
          <div className="text-center my-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th style={{width:"60px"}}>#</th>
                <th style={{}}>Name</th>
                <th style={{width:"75px"}}>Roll No</th>
                <th style={{width:"100px"}}>GR No</th>
                <th style={{width:"110px"}}>Conduct</th>
                <th style={{width:"110px"}}>Progress</th>
                <th style={{width:"150px"}}>Remark</th>
                <th style={{}}>Reason</th>                
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr key={item.studentId}>
                  <td>{index + 1}</td>
                  <td>
                    {`${item.firstName} ${item.middleName || ""} ${item.lastName || ""}`.trim()}
                  </td>
                  <td>{item.rollNo}</td>
                  <td>{item.generalRegisterNo}</td>

                  <td>
                    <select
                      value={item.lcConductMasterId || ""}
                      onChange={(e) =>
                        handleLCChange(index, "lcConductMasterId", e.target.value)
                      }
                    >
                      {conductList.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td>
                    <select
                      value={item.lcProgressMasterId || ""}
                      onChange={(e) =>
                        handleLCChange(index, "lcProgressMasterId", e.target.value)
                      }
                    >
                      {progressList.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td>
                    <select
                      value={item.lcRemarkMasterId || ""}
                      onChange={(e) =>
                        handleLCChange(index, "lcRemarkMasterId", e.target.value)
                      }
                    >
                      {remarkList.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td>
                    <select
                      value={item.lcReasonMasterId || ""}
                      onChange={(e) =>
                        handleLCChange(index, "lcReasonMasterId", e.target.value)
                      }
                    >
                      {reasonList.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </td>

                  
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      <Footer />
      <ToastContainer />
    </>
  );
};

export default StudentLCDetailMappings;
